// MenACWY has three products — Menveo®, Nimenrix®, and MenQuadfi®▼ (black
// triangle). All share a standard single-dose schedule for most patients,
// but patients with asplenia, hyposplenism, complement deficiency, or on
// eculizumab or ravulizumab require a 2-dose primary course 1 month apart.
// Both schedules are provided. MenQuadfi®▼ is a black triangle product —
// requiresYellowCardDiscussion is set true conservatively. The MenACWY NHS
// entitlement advisory for under-25s is enforced at basket selection
// (Step 6) and is not repeated here.

import { registerTier2Config } from "../tier2ConfigRegistry";
import { PGD_IDS } from "../../pgds/pgdIds";

registerTier2Config({
  pgdId: PGD_IDS.MENACWY,
  displayName: "MenACWY",
  serviceType: "administration",
  minimumAgeYears: 0,
  indicationSummary:
    "At risk through Hajj or Umrah travel (certificate required), travel to high-risk areas, or missed routine or catch-up MenACWY vaccination. Protects against meningococcal serogroups A, C, W-135, and Y only \u2014 does not protect against MenB.",

  brands: [
    {
      brandId: "menveo",
      brandName: "Menveo\u00ae",
      minimumAgeYears: 2,
      route: "IM",
      doseDescription: "0.5 mL per dose (after reconstitution)",
      administrationNotes:
        "Reconstitute by attaching the liquid component syringe to the vial containing the powder; shake vigorously. Use promptly after reconstitution (stable for up to 8 hours below 25\u00b0C if not used immediately). Administer by intramuscular injection into the deltoid muscle. Do not use the gluteal region. Inspect the reconstituted solution \u2014 it should be clear and colourless to slightly yellow.",
    },
    {
      brandId: "nimenrix",
      brandName: "Nimenrix\u00ae",
      minimumAgeYears: 0,
      route: "IM",
      doseDescription: "0.5 mL per dose (after reconstitution)",
      administrationNotes:
        "Reconstitute by adding the entire contents of the solvent vial or syringe to the vial containing the powder; shake until dissolved. Use promptly after reconstitution. Administer by intramuscular injection into the deltoid muscle. In infants under 12 months, administer into the anterolateral thigh.",
    },
    {
      brandId: "menquadfi",
      brandName: "MenQuadfi\u00ae\u25bc",
      minimumAgeYears: 1,
      route: "IM",
      doseDescription:
        "0.5 mL per dose (ready-to-use solution \u2014 no reconstitution required)",
      administrationNotes:
        "MenQuadfi\u00ae is a ready-to-use solution and does not require reconstitution. Expel any air bubbles from the syringe before administration. The solution should be clear and colourless. Administer by intramuscular injection into the deltoid muscle.",
    },
  ],

  schedules: [
    {
      scheduleId: "menacwy-standard-single",
      label: "Standard \u2014 single dose (most patients)",
      offLabel: false,
      doses: [
        {
          doseNumber: 1,
          label: "Day 0 \u2014 single dose",
        },
      ],
      completionNotes:
        "A single dose is appropriate for most patients. For patients with asplenia, hyposplenism, complement deficiency, or those on eculizumab or ravulizumab, a 2-dose primary course is required \u2014 use the 2-dose at-risk schedule instead.",
    },
    {
      scheduleId: "menacwy-two-dose-at-risk",
      label:
        "2-dose primary course \u2014 at-risk patients only (asplenia, hyposplenism, complement deficiency, eculizumab or ravulizumab treatment)",
      offLabel: false,
      doses: [
        {
          doseNumber: 1,
          label: "Day 0 \u2014 first dose",
        },
        {
          doseNumber: 2,
          label: "At least 1 month after dose 1",
          intervalFromDose1Days: 30,
        },
      ],
      boosterInfo:
        "Patients who received the 2-dose primary course due to asplenia, hyposplenism, complement deficiency, or eculizumab/ravulizumab treatment require booster doses every 5 years. Refer also for MenB and pneumococcal vaccination if not already given \u2014 these are not held under this PGD.",
      completionNotes:
        "This 2-dose schedule is only for patients with asplenia, hyposplenism, complement deficiency, or those on eculizumab or ravulizumab. For Hajj or Umrah patients in this group where time before travel is short: give dose 1 with proof of vaccination for entry and arrange dose 2 on return. Refer the patient for MenB and pneumococcal vaccination.",
    },
  ],

  capturesPriorDoseHistory: true,

  exclusions: [
    {
      checkId: "menacwy-ex-hypersensitivity",
      question:
        "Does the patient have known hypersensitivity to any component of MenACWY vaccines \u2014 including hypersensitivity to diphtheria toxoid (relevant to Menveo\u00ae) or tetanus toxoid (relevant to Nimenrix\u00ae / MenQuadfi\u00ae\u25bc)?",
      disqualifyingAnswer: "yes",
      disqualificationMessage: "MenACWY vaccine is contraindicated. Do not proceed.",
      referralMessage: "Refer to the patient's GP or an allergy specialist.",
    },
    {
      checkId: "menacwy-ex-anaphylaxis-prior",
      question:
        "Has the patient had a confirmed anaphylactic reaction to a previous MenACWY vaccine or to a vaccine containing the same carrier protein (diphtheria toxoid or tetanus toxoid)?",
      disqualifyingAnswer: "yes",
      disqualificationMessage: "MenACWY vaccine is contraindicated. Do not proceed.",
      referralMessage: "Refer to the patient's GP or an allergy specialist.",
    },
    {
      checkId: "menacwy-ex-contact-case",
      question:
        "Is this patient a known close contact of a confirmed case of meningococcal serogroup A, C, W-135, or Y disease?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "Post-exposure prophylaxis for meningococcal contacts must be arranged urgently through the NHS. Do not supply under this PGD.",
      referralMessage:
        "Refer urgently to the patient's GP or local health protection team for post-exposure prophylaxis. This is a time-critical referral.",
    },
    {
      checkId: "menacwy-ex-recent-menc",
      question: "Has the patient received a MenC or Hib/MenC vaccine within the last 4 weeks?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "A minimum interval of 1 month is required between a MenC or Hib/MenC vaccine and a MenACWY vaccine. Vaccination must be deferred until at least 1 month has elapsed since the MenC or Hib/MenC dose.",
      referralMessage: "Rebook the appointment once the 1-month interval has been reached.",
    },
    {
      checkId: "menacwy-ex-neurological",
      question: "Does the patient have an evolving or unstable neurological condition?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "Vaccination should be deferred until the neurological condition is stable.",
      referralMessage: "Refer to the patient's GP or neurologist. Rebook when the condition is stable.",
    },
  ],

  cautions: [
    {
      checkId: "menacwy-caut-bleeding",
      question:
        "Does the patient have a bleeding disorder or is the patient on anticoagulant therapy?",
      cautionMessage:
        "Administer by intramuscular injection with care using a fine-gauge needle with firm pressure applied for at least two minutes afterwards. For patients on stable warfarin with INR within therapeutic range, IM injection may proceed. Document the clinical decision.",
    },
    {
      checkId: "menacwy-caut-asplenia-complement",
      question:
        "Does the patient have asplenia, hyposplenism, complement deficiency, or are they receiving treatment with eculizumab or ravulizumab?",
      cautionMessage:
        "These patients are at increased risk of invasive meningococcal disease and may have a reduced immune response to MenACWY vaccine. A 2-dose primary course (at least 1 month apart) is required instead of the standard single dose \u2014 select the 2-dose at-risk schedule. Boosters are required every 5 years. Refer also for MenB and pneumococcal vaccination if not already in place \u2014 these are outside this PGD. Reinforce the importance of completing the 2-dose course even if travel is imminent.",
    },
    {
      checkId: "menacwy-caut-immunosuppressed",
      question:
        "Is the patient immunosuppressed through disease or treatment (other than the conditions listed in the asplenia/complement caution above)?",
      cautionMessage:
        "Immunosuppression may reduce the immune response to MenACWY vaccine. Vaccination should still proceed \u2014 the vaccine is inactivated and safe. Counsel that protection may be suboptimal. Seek specialist advice if the immunosuppression is severe.",
    },
    {
      checkId: "menacwy-caut-pregnancy",
      question: "Is the patient pregnant or breastfeeding?",
      cautionMessage:
        "MenACWY vaccine may be given in pregnancy and whilst breastfeeding where the risk of meningococcal disease is significant. Advise the patient to inform their GP or midwife. Document the risk-benefit discussion.",
    },
    {
      checkId: "menacwy-caut-tetanus-toxoid",
      question:
        "Is the patient also receiving a tetanus-toxoid-containing vaccine (e.g. Revaxis\u00ae) at this visit or in the near future? (Relevant if Nimenrix\u00ae or MenQuadfi\u00ae\u25bc has been selected.)",
      cautionMessage:
        "If Nimenrix\u00ae or MenQuadfi\u00ae\u25bc has been selected: co-administer both vaccines at the same visit (at separate sites), or give MenACWY at least 1 month before the tetanus-toxoid-containing vaccine. This restriction does not apply to Menveo\u00ae. Ensure the co-administration constraint has been reviewed in the Travel Health Plan.",
    },
    {
      checkId: "menacwy-caut-syncope",
      question:
        "Is there any history of syncope (fainting) following a previous injection or vaccination?",
      cautionMessage:
        "Syncope can occur following vaccination. Ensure the patient is seated or lying down during and for the duration of the observation period after administration.",
    },
  ],

  supplyGates: [],

  requiresICVP: false,
  requiresLariamCard: false,
  // MenQuadfi® is a black triangle product — Yellow Card discussion
  // applies if MenQuadfi® was the selected brand.
  requiresYellowCardDiscussion: true,
  requiresCHISNotificationUnder19: true,
  requiresGPNotification: true,
  standardObservationMinutes: 15,
  extendedObservationMinutes: 30,

  patientAdvicePoints: [
    "This vaccine protects against meningococcal serogroups A, C, W-135, and Y only. It does not protect against meningococcal serogroup B \u2014 a separate MenB vaccine is available but is not given under this service.",
    "For Hajj or Umrah travel: a MenACWY vaccination certificate is required for entry to Saudi Arabia. Confirm the Saudi Ministry of Health time window requirements apply to your travel dates.",
    "If you have asplenia, hyposplenism, complement deficiency, or are taking eculizumab or ravulizumab: you will need a second dose of MenACWY at least 1 month after today's dose. Please ensure you return for this appointment. You will also need booster doses every 5 years and should discuss MenB and pneumococcal vaccination with your GP.",
    "Seek urgent medical attention if you develop any of the following: high fever, severe headache, stiff neck, dislike of bright lights, a rash that does not fade when pressed, vomiting, or confusion. These may be signs of meningococcal disease \u2014 call 999 or go to A&E immediately.",
    "The vaccine takes approximately 7\u201310 days to provide protection.",
    "If MenQuadfi\u00ae\u25bc was used: please report any suspected side effects to the MHRA via the Yellow Card scheme at yellowcard.mhra.gov.uk or by calling 0800 731 6789.",
  ],
});
